"use client"

import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { useTranslations } from 'next-intl'

const Content = () => {
    const t = useTranslations('TermometroSocial');

    // Refs for scroll animations
    const mainRef = useRef(null)
    const featuresRef = useRef(null)

    // Check if elements are in view
    const mainInView = useInView(mainRef, { once: true, margin: "-100px" })
    const featuresInView = useInView(featuresRef, { once: true, margin: "-100px" })

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.5,
                delayChildren: 0.3
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8
            }
        }
    }

    const cardVariants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    // Individual card variants with specific delays
    const card1Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.2
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const card2Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.7
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const card3Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 1.2
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }


    return (
        <div className="">
            <motion.div
                ref={mainRef}
                initial="hidden"
                animate={mainInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <motion.h3
                    className="text-3xl font-normal text-red-600 dark:text-red-400 mb-8 italic -mt-8"
                    variants={itemVariants}
                >
                    {t('main_title')}
                </motion.h3>


            </motion.div>

            {/* Análisis de sentimientos section */}
            <motion.div
                className="mb-8"
                initial="hidden"
                animate={mainInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={itemVariants}
                >
                    <h4 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-4">{t('sentiment_analysis.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('sentiment_analysis.description')}
                    </p>
                </motion.div>
            </motion.div>

            <motion.div
                ref={featuresRef}
                className="grid md:grid-cols-2 gap-8"
                initial="hidden"
                animate={featuresInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                {/* Informes claros y accionables */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card1Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.clear_reports.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.clear_reports.description')}
                    </p>
                </motion.div>

                {/* Evaluación de impacto */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card2Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.impact_evaluation.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.impact_evaluation.description')}
                    </p>
                </motion.div>

                {/* Tracker de temas */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card3Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.topic_tracker.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.topic_tracker.description')}
                    </p>
                </motion.div>

                {/* Seguimiento de opinión */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={cardVariants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.opinion_tracking.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.opinion_tracking.description')}
                    </p>
                </motion.div>
            </motion.div>

        </div>
    )
}

export default Content
